import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { create } from "zustand";
import type { UserInfo, UserToken } from "#/entity";
import type { ApiError } from "@/api/apiClient";
import userService, { type SignInReq } from "@/api/services/userService";
import {
	USER_TOKEN_STORAGE_KEY,
	clearPersistedUserToken,
	readPersistedUserToken,
	writePersistedUserToken,
} from "./sessionStorage";

type UserStore = {
	userInfo: Partial<UserInfo>;
	userToken: UserToken;
	sessionReady: boolean;

	actions: {
		setUserInfo: (userInfo: UserInfo) => void;
		setUserToken: (token: UserToken) => void;
		setSessionReady: (ready: boolean) => void;
		clearUserInfoAndToken: () => void;
	};
};

const removeLegacyPersistedUserStore = () => {
	try {
		localStorage.removeItem("userStore");
	} catch {
		// Storage can be unavailable in privacy-restricted browser contexts.
	}
};

removeLegacyPersistedUserStore();

const useUserStore = create<UserStore>()((set) => ({
	userInfo: {},
	userToken: {},
	sessionReady: false,
	actions: {
		setUserInfo: (userInfo) => {
			set({ userInfo });
		},
		setUserToken: (userToken) => {
			writePersistedUserToken(userToken);
			set({ userToken });
		},
		setSessionReady: (sessionReady) => {
			set({ sessionReady });
		},
		clearUserInfoAndToken() {
			clearPersistedUserToken();
			set({ userInfo: {}, userToken: {} });
		},
	},
}));

export const useUserInfo = () => useUserStore((state) => state.userInfo);
export const useUserToken = () => useUserStore((state) => state.userToken);
export const useSessionReady = () => useUserStore((state) => state.sessionReady);
export const useUserPermissions = () => useUserStore((state) => state.userInfo.permissions || []);
export const useUserRoles = () => useUserStore((state) => state.userInfo.roles || []);
export const useUserActions = () => useUserStore((state) => state.actions);

let initializeSessionRequest: Promise<void> | null = null;

export const initializeUserSession = (): Promise<void> => {
	if (!initializeSessionRequest) {
		initializeSessionRequest = (async () => {
			const { actions } = useUserStore.getState();
			const { refreshToken } = readPersistedUserToken();
			try {
				if (!refreshToken) return;
				actions.setUserToken(await userService.refresh(refreshToken));
			} catch {
				actions.clearUserInfoAndToken();
			} finally {
				actions.setSessionReady(true);
			}
		})();
	}

	return initializeSessionRequest;
};

if (typeof window !== "undefined") {
	window.addEventListener("storage", (event) => {
		if (event.key !== USER_TOKEN_STORAGE_KEY) return;

		const token = readPersistedUserToken();
		if (token.accessToken && token.refreshToken) {
			useUserStore.setState({ userToken: token });
		} else {
			useUserStore.setState({ userInfo: {}, userToken: {} });
		}
	});
}

export const useSignIn = () => {
	const { setUserToken, setUserInfo } = useUserActions();

	const signInMutation = useMutation({
		mutationFn: userService.signin,
	});

	const signIn = async (data: SignInReq) => {
		try {
			const res = await signInMutation.mutateAsync(data);
			const { user, accessToken, refreshToken } = res;
			setUserToken({ accessToken, refreshToken });
			setUserInfo(user);
		} catch (err) {
			const error = err as ApiError;
			if (!error.notified) {
				toast.error(error.message, {
					position: "top-center",
				});
			}
			throw err;
		}
	};

	return signIn;
};

export default useUserStore;
